import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../utils/type';
import {
  selectVendorChargingReport,
  selectVendorChargingReportLoading,
  selectVendorChargingReportError,
  selectVendorChargingReportDownloadError,
} from '../services/chargingReport/selectors';
import {
  getVendorChargingReport,
  downloadVendorChargingReport,
} from '../services/chargingReport/crudFunctions';
import { VendorChargingReportFilters } from '../model/vendorReports';
import LoadingSpinner from '../../../elements/LoadingSpinner';
import Pagination from '../../../elements/Pagination';
import StationSelect from '../../../elements/StationSelect';

const PAGE_SIZE = 10;

const VendorChargingReport: React.FC = () => {
  const dispatch = useDispatch();
  const data = useSelector((s: RootState) => selectVendorChargingReport(s));
  const loading = useSelector((s: RootState) => selectVendorChargingReportLoading(s));
  const error = useSelector((s: RootState) => selectVendorChargingReportError(s));
  const downloadError = useSelector((s: RootState) => selectVendorChargingReportDownloadError(s));

  const [filters, setFilters] = useState<VendorChargingReportFilters>({});
  const [format, setFormat] = useState<'csv' | 'xls'>('csv');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  useEffect(() => {
    dispatch<any>(getVendorChargingReport({}));
  }, [dispatch]);

  useEffect(() => { setPage(1); }, [data]);

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch<any>(getVendorChargingReport(filters));
  };

  const handleDownload = () => {
    dispatch<any>(downloadVendorChargingReport({ ...filters, format }));
  };

  const fmt = (n?: number | string) => {
    const num = Number(n);
    return !isNaN(num) ? `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—';
  };

  const fmtDate = (s?: string): string => {
    if (!s) return '—';
    const d = new Date(s);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    const secs = String(d.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${mins}:${secs}`;
  };

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      Completed: 'bg-success', Failed: 'bg-danger',
      Started: 'bg-primary', Cancelled: 'bg-secondary',
    };
    return <span className={`badge ${map[status] || 'bg-secondary'}`}>{status}</span>;
  };

  const sessions = data?.sessions ?? [];
  const pagedSessions = sessions.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <div className="page-title">
        <div className="title_left">
          <h3>
            <i className="bi bi-file-earmark-bar-graph me-2 text-success" />
            Charging Report
          </h3>
        </div>
      </div>

      {/* Filters */}
      <div className="x_panel">
        <div className="x_title">
          <h4>Filters</h4>
          <div className="clearfix" />
        </div>
        <div className="x_content">
          {(error || downloadError) && (
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle me-2" />
              {error || downloadError}
            </div>
          )}
          <form onSubmit={handleFilter}>
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <label className="form-label">Start Date</label>
                <input type="date" className="form-control"
                  value={filters.startDate || ''}
                  onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value || undefined }))}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">End Date</label>
                <input type="date" className="form-control"
                  value={filters.endDate || ''}
                  onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value || undefined }))}
                />
              </div>
              <div className="col-md-3">
                <StationSelect
                  vendorId={undefined}
                  value={filters.stationId}
                  onChange={(id) => setFilters((f) => ({ ...f, stationId: id }))}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Charger ID</label>
                <input type="text" className="form-control" placeholder="e.g. GC-CHG-001"
                  value={filters.chargerId || ''}
                  onChange={(e) => setFilters((f) => ({ ...f, chargerId: e.target.value || undefined }))}
                />
              </div>
            </div>
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <label className="form-label">User ID</label>
                <input type="number" className="form-control" placeholder="All"
                  value={filters.userId || ''}
                  onChange={(e) => setFilters((f) => ({ ...f, userId: e.target.value ? Number(e.target.value) : undefined }))}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Format</label>
                <select className="form-select" value={format}
                  onChange={(e) => setFormat(e.target.value as 'csv' | 'xls')}>
                  <option value="csv">CSV</option>
                  <option value="xls">XLS</option>
                </select>
              </div>
            </div>
            <div className="row g-3">
              <div className="col-12 d-flex gap-2">
                <button type="submit" className="btn btn-success px-4">
                  <i className="fas fa-search me-2" /> Apply Filters
                </button>
                <button type="button" className="btn px-4" onClick={handleDownload}
                  style={{ background: 'var(--gt-secondary)', color: '#fff', border: 'none' }}>
                  <i className="fas fa-download me-2" /> Download
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Summary Cards */}
          {data?.summary && (
            <div className="row gy-3 mb-4">
              {[
                { label: 'Total Sessions', value: data.summary.totalSessions, icon: 'bi-lightning', color: 'bg-primary' },
                { label: 'Completed', value: data.summary.completedSessions, icon: 'bi-check-circle', color: 'bg-success' },
                { label: 'Total Amount', value: fmt(data.summary.totalAmount), icon: 'bi-currency-rupee', color: 'bg-info' },
                { label: 'Energy (kWh)', value: `${Number(data.summary.totalEnergyConsumed)?.toFixed(2)} kWh`, icon: 'bi-lightning-charge', color: 'bg-warning' },
                { label: 'Platform Commission', value: fmt(data.summary.totalPlatformCommission), icon: 'bi-percent', color: 'bg-danger' },
                { label: 'Vendor Payout', value: fmt(data.summary.totalVendorPayout), icon: 'bi-wallet2', color: 'bg-secondary' },
              ].map((card) => (
                <div className="col-xl-2 col-md-4 col-sm-6" key={card.label}>
                  <div className="card h-100">
                    <div className="card-body py-3 px-2">
                      <div className="d-flex align-items-center overflow-hidden">
                        <div
                          className={`${card.color} text-white rounded-circle d-flex align-items-center justify-content-center me-2`}
                          style={{ width: 40, height: 40, flexShrink: 0 }}
                        >
                          <i className={`bi ${card.icon}`} />
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
                            className="text-muted small text-uppercase text-truncate"
                            title={card.label}
                          >
                            {card.label}
                          </div>
                          <div
                            className="fw-bold text-truncate"
                            title={String(card.value)}
                          >
                            {card.value}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Sessions Table */}
          {sessions.length > 0 ? (
            <div className="x_panel">
              <div className="x_title">
                <h4>Sessions ({sessions.length} total)</h4>
                <div className="clearfix" />
              </div>
              <div className="x_content">
                <div className="table-responsive">
                  <table className="table table-striped table-hover table-bordered">
                    <thead className="table-dark">
                      <tr>
                        <th>#</th>
                        <th>Session ID</th>
                        <th>Transaction</th>
                        <th>User</th>
                        <th>Mobile</th>
                        <th>Station</th>
                        <th>Charger</th>
                        <th>Connector</th>
                        <th>Status</th>
                        <th>Start</th>
                        <th>End</th>
                        <th>Energy</th>
                        <th>Amount</th>
                        <th>GST</th>
                        <th>Final</th>
                        <th>Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                        {pagedSessions.map((s: any, idx: number) => (
                          <tr key={s.id}>
                            <td>{(page - 1) * pageSize + idx + 1}</td>
                            <td>{s.id}</td>
                            <td>{s.transaction_id}</td>
                            <td>{s.user_name}</td>
                            <td>{s.user_mobile}</td>
                            <td>{s.station_name}</td>
                            <td>{s.charger_id}</td>
                            <td>{s.connector_id}</td>
                            <td>{statusBadge(s.status)}</td>
                            <td>{fmtDate(s.start_time)}</td>
                            <td>{fmtDate(s.end_time)}</td>
                            <td>{Number(s.energy_consumed)?.toFixed(2)} kWh</td>
                            <td>{fmt(s.total_amount)}</td>
                            <td>{fmt(s.total_gst)}</td>
                            <td>{fmt(s.final_amount)}</td>
                            <td>{fmt(s.platform_commission)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  currentPage={page}
                  totalItems={sessions.length}
                  pageSize={pageSize}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              </div>
            </div>
          ) : (
            !loading && (
              <div className="x_panel">
                <div className="x_content text-center py-5 text-muted">
                  <i className="bi bi-inbox fs-1 d-block mb-2" />
                  No sessions found for the selected filters.
                </div>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

export default VendorChargingReport;
